import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusTag } from '@/components/ui';
import type { Organization } from '@/api/orgAdmin';
import { OrgAvatar } from './OrgAvatar';
import { getOrgStatusConfig, formatCountVN, formatDateVN, orgHealth } from './orgFormat';

interface OrgDetailHeaderProps {
    org: Organization;
    tenantCount: number;
    accountCount: number | undefined;
    levelCount: number | undefined;
    onViewHierarchy: () => void;
    onAddTenant: () => void;
    canAddTenant: boolean;
}

/**
 * EAA-1815: `/organizations/:id` header block — frame 22 lines 10-121.
 *
 * "N tài khoản" is `ProvisioningStatus.assignments.active` (the org's real,
 * currently-active grants — `GET /organizations/{id}/provisioning-status`,
 * already powering `HierarchyPage`'s coverage card) and "N Level" is
 * `GET /organizations/{id}/levels`'s paginated `count`. Neither field is on
 * `OrganizationSerializer` itself, so both render "—" (`formatCountVN`) until
 * their query resolves, rather than a placeholder number.
 */
export function OrgDetailHeader({
    org,
    tenantCount,
    accountCount,
    levelCount,
    onViewHierarchy,
    onAddTenant,
    canAddTenant,
}: OrgDetailHeaderProps) {
    const { t } = useTranslation();
    const health = orgHealth(org.is_active, tenantCount);
    const statusConfig = useMemo(() => getOrgStatusConfig(t), [t]);
    const brandColor = org.branding?.primary_color;

    return (
        <div className="portal-org-detail-header">
            <OrgAvatar
                name={org.name}
                size={48}
                radius={12}
                background={brandColor || 'var(--color-primary)'}
                color="#fff"
                fontSize={17}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 0 }}>
                <span className="portal-org-detail-titlerow">
                    <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>
                        {org.name}
                    </h1>
                    <StatusTag status={health} config={statusConfig} />
                </span>
                <span className="portal-org-detail-meta">
                    <code style={{ fontFamily: 'var(--font-mono)' }}>{org.code}</code>
                    <span>
                        {t('admin.organizations.detail.header.tenantsCountLabel', {
                            value: formatCountVN(tenantCount),
                        })}
                    </span>
                    <span>
                        {t('admin.organizations.detail.header.accountsCountLabel', {
                            value: formatCountVN(accountCount),
                        })}
                    </span>
                    <span>
                        {t('admin.organizations.detail.header.levelsCountLabel', {
                            value: formatCountVN(levelCount),
                        })}
                    </span>
                    <span>
                        {t('admin.organizations.detail.header.createdLabel', {
                            date: formatDateVN(org.created_at),
                        })}
                    </span>
                </span>
            </div>
            <span className="portal-org-detail-actions">
                <button type="button" className="portal-btn" onClick={onViewHierarchy}>
                    {t('admin.organizations.detail.header.viewHierarchy')}
                </button>
                {canAddTenant && (
                    <button
                        type="button"
                        className="portal-btn portal-btn-primary"
                        onClick={onAddTenant}
                    >
                        {t('admin.organizations.addTenantAction')}
                    </button>
                )}
            </span>
        </div>
    );
}

export default OrgDetailHeader;
